// End-to-end encryption for DMs and groups.
// Every account has one ECDH P-256 identity key. The private key never leaves the device unencrypted:
// it is stored in IndexedDB and, for other devices, uploaded as a backup encrypted with the user's
// encryption password (PBKDF2-SHA256 + AES-GCM). Each message gets a fresh AES-256-GCM key that is
// wrapped for every chat member with a key derived from ECDH(sender, member) + HKDF.
type Api = (url: string, method?: string, body?: unknown) => Promise<any>;
type Jwk = { kty: 'EC'; crv: 'P-256'; x: string; y: string; d?: string };
export const PREFIX = 'e2ee:v1:';
const ITER = 600000;
const enc = new TextEncoder(), dec = new TextDecoder();
const subtle = () => { if (!globalThis.crypto?.subtle) throw new Error('Verschlüsselung braucht eine sichere Verbindung (HTTPS).'); return crypto.subtle; };
const b64 = (u: ArrayBuffer | Uint8Array) => { const a = u instanceof Uint8Array ? u : new Uint8Array(u); let s = ''; for (let i = 0; i < a.length; i += 0x8000) s += String.fromCharCode(...a.subarray(i, i + 0x8000)); return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, ''); };
const unb64 = (s: string) => { const t = atob(s.replace(/-/g, '+').replace(/_/g, '/') + '==='.slice((s.length + 3) % 4)); const a = new Uint8Array(t.length); for (let i = 0; i < t.length; i++) a[i] = t.charCodeAt(i); return a; };
const rand = (n: number) => crypto.getRandomValues(new Uint8Array(n));
export const isEnvelope = (s: unknown): s is string => typeof s === 'string' && s.startsWith(PREFIX);

// ---------- IndexedDB key storage (per account) ----------
function idb(): Promise<IDBDatabase> {
  return new Promise((ok, bad) => { const r = indexedDB.open('malvo-e2ee', 1); r.onupgradeneeded = () => r.result.createObjectStore('keys'); r.onsuccess = () => ok(r.result); r.onerror = () => bad(r.error); });
}
async function idbGet(k: string): Promise<any> { const d = await idb(); return new Promise((ok, bad) => { const r = d.transaction('keys').objectStore('keys').get(k); r.onsuccess = () => ok(r.result); r.onerror = () => bad(r.error); }); }
async function idbSet(k: string, v: unknown) { const d = await idb(); return new Promise<void>((ok, bad) => { const t = d.transaction('keys', 'readwrite'); t.objectStore('keys').put(v, k); t.oncomplete = () => ok(); t.onerror = () => bad(t.error); }); }
async function idbDel(k: string) { const d = await idb(); return new Promise<void>((ok, bad) => { const t = d.transaction('keys', 'readwrite'); t.objectStore('keys').delete(k); t.oncomplete = () => ok(); t.onerror = () => bad(t.error); }); }

// ---------- State ----------
let account = '';
let identity: { fp: string; priv: CryptoKey; pub: Jwk } | null = null;
let loaded: Promise<void> | null = null;
const listeners = new Set<() => void>();
let tick = 0;
const changed = () => { tick++; listeners.forEach(f => f()); };
export const subscribe = (f: () => void) => { listeners.add(f); return () => { listeners.delete(f); }; };
export const version = () => tick;

export async function fingerprintOf(pub: Jwk) { const h = new Uint8Array(await subtle().digest('SHA-256', enc.encode(pub.x + '.' + pub.y))); return [...h.slice(0, 16)].map(b => b.toString(16).padStart(2, '0')).join(''); }
const importPriv = (j: Jwk) => subtle().importKey('jwk', { ...j, key_ops: ['deriveBits'], ext: true } as JsonWebKey, { name: 'ECDH', namedCurve: 'P-256' }, false, ['deriveBits']);
const pubCache = new Map<string, Promise<CryptoKey>>();
const importPub = (j: Jwk) => { const k = j.x + j.y; if (!pubCache.has(k)) pubCache.set(k, subtle().importKey('jwk', { kty: 'EC', crv: 'P-256', x: j.x, y: j.y, ext: true } as JsonWebKey, { name: 'ECDH', namedCurve: 'P-256' }, false, [])); return pubCache.get(k)!; };

export function setAccount(id: string | null) { if ((id || '') === account) return; account = id || ''; identity = null; loaded = null; plain.clear(); pending.clear(); memberCache.clear(); changed(); void loadLocal(); }
export function ready() { return !!identity; }
export function myFingerprint() { return identity?.fp || ''; }
async function loadLocal() {
  if (!account) return;
  loaded ||= (async () => { try { const j = await idbGet('id:' + account) as Jwk | undefined; if (j?.d) identity = { fp: await fingerprintOf(j), priv: await importPriv(j), pub: { kty: 'EC', crv: 'P-256', x: j.x, y: j.y } }; } catch {} changed(); })();
  return loaded;
}

// Server status: none (never set up), locked (set up, but this device has no key), ready, mismatch (local key is outdated).
export async function status(api: Api): Promise<{ state: 'none' | 'locked' | 'ready'; fp: string | null }> {
  await loadLocal();
  const me = await api('/e2ee/me');
  if (!me.fp) return { state: 'none', fp: null };
  if (identity?.fp === me.fp) return { state: 'ready', fp: me.fp };
  if (identity) { identity = null; await idbDel('id:' + account).catch(() => {}); changed(); }
  return { state: 'locked', fp: me.fp };
}

async function passwordKey(password: string, salt: Uint8Array, iter: number) {
  const base = await subtle().importKey('raw', enc.encode(password.normalize('NFKC')), 'PBKDF2', false, ['deriveKey']);
  return subtle().deriveKey({ name: 'PBKDF2', hash: 'SHA-256', salt: salt as BufferSource, iterations: iter }, base, { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
}
async function backupOf(j: Jwk, password: string) {
  const salt = rand(16), iv = rand(12), key = await passwordKey(password, salt, ITER);
  const ct = await subtle().encrypt({ name: 'AES-GCM', iv: iv as BufferSource }, key, enc.encode(JSON.stringify({ x: j.x, y: j.y, d: j.d })));
  return { v: 1, iter: ITER, salt: b64(salt), iv: b64(iv), ct: b64(ct) };
}
async function store(j: Jwk) { await idbSet('id:' + account, j); identity = { fp: await fingerprintOf(j), priv: await importPriv(j), pub: { kty: 'EC', crv: 'P-256', x: j.x, y: j.y } }; loaded = Promise.resolve(); plain.forEach((v, k) => { if (v === FAIL || v === LOCKED) plain.delete(k); }); changed(); }

// Creates a new identity (first setup, or reset after a forgotten password).
export async function create(api: Api, password: string) {
  const pair = await subtle().generateKey({ name: 'ECDH', namedCurve: 'P-256' }, true, ['deriveBits']) as CryptoKeyPair;
  const j = await subtle().exportKey('jwk', pair.privateKey) as Jwk;
  await api('/e2ee/me', 'PUT', { publicKey: { kty: 'EC', crv: 'P-256', x: j.x, y: j.y }, backup: await backupOf(j, password) });
  await store(j);
}
// Unlocks this device with the backup from the server.
export async function unlock(api: Api, password: string) {
  const me = await api('/e2ee/me');
  if (!me.backup) throw new Error('Keine Sicherung vorhanden.');
  let j: Jwk;
  try { const key = await passwordKey(password, unb64(me.backup.salt), me.backup.iter); j = { kty: 'EC', crv: 'P-256', ...JSON.parse(dec.decode(await subtle().decrypt({ name: 'AES-GCM', iv: unb64(me.backup.iv) as BufferSource }, key, unb64(me.backup.ct) as BufferSource))) }; }
  catch { throw new Error('Falsches Verschlüsselungspasswort.'); }
  if (await fingerprintOf(j) !== me.fp) throw new Error('Die Sicherung passt nicht zum aktuellen Schlüssel.');
  await store(j);
}
export async function changePassword(api: Api, password: string) {
  const j = await idbGet('id:' + account) as Jwk;
  if (!j?.d) throw new Error('Dieses Gerät ist nicht entsperrt.');
  await api('/e2ee/me', 'PUT', { publicKey: { kty: 'EC', crv: 'P-256', x: j.x, y: j.y }, backup: await backupOf(j, password) });
}
export async function forgetDevice() { if (account) await idbDel('id:' + account).catch(() => {}); identity = null; loaded = null; plain.clear(); changed(); }

// ---------- Message crypto ----------
async function wrapKey(priv: CryptoKey, pub: Jwk, salt: Uint8Array, info: string) {
  const bits = await subtle().deriveBits({ name: 'ECDH', public: await importPub(pub) } as EcdhKeyDeriveParams, priv, 256);
  const hk = await subtle().importKey('raw', bits, 'HKDF', false, ['deriveKey']);
  return subtle().deriveKey({ name: 'HKDF', hash: 'SHA-256', salt: salt as BufferSource, info: enc.encode('malvo-e2ee-v1|' + info) }, hk, { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
}
type Member = { userId: string; fp: string | null; publicKey: Jwk | null };
const memberCache = new Map<string, { at: number; list: Promise<{ enabled: boolean; members: Member[] }> }>();
export function chatKeys(api: Api, roomId: string, fresh = false): Promise<{ enabled: boolean; members: Member[] }> {
  const c = memberCache.get(roomId);
  if (!fresh && c && Date.now() - c.at < 60000) return c.list;
  const list = api('/conversations/' + roomId + '/e2ee');
  memberCache.set(roomId, { at: Date.now(), list });
  list.catch(() => memberCache.delete(roomId));
  return list;
}
export const forgetChat = (roomId?: string) => roomId ? memberCache.delete(roomId) : memberCache.clear();

// Encrypts a message for every current member of the chat (including the sender's other devices).
export async function encrypt(api: Api, roomId: string, senderId: string, text: string) {
  await loadLocal();
  if (!identity) throw new Error('Entsperre zuerst die Verschlüsselung auf diesem Gerät.');
  let { members } = await chatKeys(api, roomId);
  if (members.some(m => !m.publicKey) || (members.find(m => m.userId === senderId)?.fp || identity.fp) !== identity.fp) members = (await chatKeys(api, roomId, true)).members;
  if (members.some(m => !m.publicKey)) { forgetChat(roomId); throw new Error('Nicht alle Teilnehmer haben die Verschlüsselung eingerichtet.'); }
  const me = members.find(m => m.userId === senderId);
  if (me && me.fp !== identity.fp) { forgetChat(roomId); throw new Error('Dein Schlüssel hat sich geändert. Bitte lade MALVO neu.'); }
  noteKeys(members);
  const salt = rand(16), iv = rand(12), aad = enc.encode(roomId + '|' + senderId);
  const content = await subtle().generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt']);
  const raw = new Uint8Array(await subtle().exportKey('raw', content));
  const ct = await subtle().encrypt({ name: 'AES-GCM', iv: iv as BufferSource, additionalData: aad as BufferSource }, content, enc.encode(text));
  const k: Record<string, string> = {};
  for (const m of members) {
    const wiv = rand(12), wk = await wrapKey(identity.priv, m.publicKey!, salt, roomId + '|' + senderId + '|' + m.userId);
    const w = new Uint8Array(await subtle().encrypt({ name: 'AES-GCM', iv: wiv as BufferSource }, wk, raw));
    k[m.userId] = m.fp!.slice(0, 8) + '.' + b64(wiv) + '.' + b64(w);
  }
  return PREFIX + b64(enc.encode(JSON.stringify({ f: identity.fp, s: b64(salt), i: b64(iv), c: b64(ct), k })));
}

const keyByFp = new Map<string, Promise<Jwk>>();
const senderKey = (api: Api, uid: string, fp: string) => { const k = uid + ':' + fp; if (!keyByFp.has(k)) { const p = api('/e2ee/keys/' + uid + '/' + fp).then((r: any) => r.publicKey as Jwk); keyByFp.set(k, p); p.catch(() => keyByFp.delete(k)); } return keyByFp.get(k)!; };

export async function decrypt(api: Api, body: string, roomId: string, authorId: string, meId: string) {
  await loadLocal();
  if (!identity) throw new Error('locked');
  const e = JSON.parse(dec.decode(unb64(body.slice(PREFIX.length))));
  const entry = e.k?.[meId];
  if (!entry) throw new Error('not-for-me');
  const [fp8, wiv, w] = String(entry).split('.');
  if (fp8 !== identity.fp.slice(0, 8)) throw new Error('old-key');
  const sender = await senderKey(api, authorId, e.f);
  if (await fingerprintOf(sender) !== e.f) throw new Error('bad-key');
  const wk = await wrapKey(identity.priv, sender, unb64(e.s), roomId + '|' + authorId + '|' + meId);
  const raw = await subtle().decrypt({ name: 'AES-GCM', iv: unb64(wiv) as BufferSource }, wk, unb64(w) as BufferSource);
  const content = await subtle().importKey('raw', raw, 'AES-GCM', false, ['decrypt']);
  return dec.decode(await subtle().decrypt({ name: 'AES-GCM', iv: unb64(e.i) as BufferSource, additionalData: enc.encode(roomId + '|' + authorId) as BufferSource }, content, unb64(e.c) as BufferSource));
}

// ---------- Synchronous view cache for rendering ----------
const FAIL = '\u0000fail', LOCKED = '\u0000locked';
const plain = new Map<string, string>();
const pending = new Set<string>();
let apiRef: Api | null = null, meRef = '';
export function configure(api: Api, meId: string) { apiRef = api; meRef = meId; } // encryption is off: no key setup or password prompt

// ---------- Automatic setup with the account password ----------
let loginPassword = '';
let autoRun: Promise<void> | null = null;
export type AutoState = 'unknown' | 'ready' | 'needs-password' | 'password-changed';
let autoState: AutoState = 'unknown';
export const state = () => autoState;
// Called by the login form: the key is created/unlocked right after sign-in, without any extra step.
export function rememberPassword(pw: string) { loginPassword = pw; autoRun = null; }
export function auto(api: Api) {
  autoRun ||= (async () => {
    try {
      const s = await status(api);
      if (s.state === 'ready') { autoState = 'ready'; return; }
      if (!loginPassword) { autoState = 'needs-password'; return; }
      const pw = loginPassword;
      if (s.state === 'none') { await create(api, pw); autoState = 'ready'; }
      else { try { await unlock(api, pw); autoState = 'ready'; } catch { autoState = 'password-changed'; } }
    } catch { autoState = autoState === 'unknown' ? 'needs-password' : autoState; }
    finally { loginPassword = ''; changed(); }
  })();
  return autoRun;
}
// Unlock or create with the account password (checked by the server first).
export async function withAccountPassword(api: Api, pw: string, reset = false) {
  await api('/e2ee/verify-password', 'POST', { password: pw });
  const s = await status(api);
  if (s.state === 'none' || reset) await create(api, pw);
  else if (s.state === 'locked') await unlock(api, pw);
  autoState = 'ready'; changed();
}
// Settings password change: keep the key, protect it with the new password.
export async function passwordChanged(api: Api, oldPw: string, newPw: string) {
  try { const s = await status(api); if (s.state === 'locked') await unlock(api, oldPw); if (s.state !== 'none') await changePassword(api, newPw); } catch {}
}

// ---------- Automatic chat encryption ----------
const enabling = new Map<string, Promise<boolean>>();
// True when the chat is (now) encrypted. Turns it on by itself once every member has a key.
export function ensureChat(_api: Api, _roomId: string): Promise<boolean> { return Promise.resolve(false); }
function ensureChatOld(api: Api, roomId: string): Promise<boolean> {
  if (!enabling.has(roomId)) {
    const p = (async () => {
      let info = await chatKeys(api, roomId);
      if (!info.enabled && info.members.some(m => !m.publicKey)) info = await chatKeys(api, roomId, true);
      if (info.enabled) return true;
      if (!identity || info.members.some(m => !m.publicKey)) return false;
      try { await api('/conversations/' + roomId + '/e2ee', 'POST'); forgetChat(roomId); return true; } catch { return false; }
    })();
    enabling.set(roomId, p);
    p.finally(() => setTimeout(() => enabling.delete(roomId), 30000));
  }
  return enabling.get(roomId)!;
}

// ---------- Message payload with files ----------
export type FileMeta = { id: string; k: string; iv: string; name: string; mime: string; size: number };
const PACK = '\u0001';
export const pack = (text: string, files: FileMeta[]) => PACK + JSON.stringify({ t: text, f: files });
export const isPacked = (s: string) => s.startsWith(PACK);
export function unpack(s: string): { text: string; files: FileMeta[] } {
  if (!isPacked(s)) return { text: s, files: [] };
  try { const j = JSON.parse(s.slice(1)); return { text: String(j.t || ''), files: Array.isArray(j.f) ? j.f : [] }; } catch { return { text: '', files: [] }; }
}
export const MAX_ENCRYPTED_FILE = 200 * 1024 * 1024;
export async function encryptFile(file: File): Promise<{ blob: File; meta: Omit<FileMeta, 'id'> }> {
  if (file.size > MAX_ENCRYPTED_FILE) throw new Error('Verschlüsselte Dateien dürfen höchstens 200 MB groß sein.');
  const key = await subtle().generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt']), iv = rand(12);
  const ct = await subtle().encrypt({ name: 'AES-GCM', iv: iv as BufferSource }, key, await file.arrayBuffer());
  return { blob: new File([ct], 'encrypted.bin', { type: 'application/octet-stream' }), meta: { k: b64(await subtle().exportKey('raw', key)), iv: b64(iv), name: file.name || 'file', mime: file.type || 'application/octet-stream', size: file.size } };
}
const fileUrls = new Map<string, string | null>();
// Blob URL of a decrypted attachment, or undefined while it is loading (null when it failed).
export function fileUrl(meta: FileMeta): string | null | undefined {
  if (fileUrls.has(meta.id)) return fileUrls.get(meta.id);
  if (!pendingFiles.has(meta.id)) {
    pendingFiles.add(meta.id);
    (async () => {
      const r = await fetch('/api/files/' + meta.id, { credentials: 'same-origin' });
      if (!r.ok) throw new Error('download');
      const key = await subtle().importKey('raw', unb64(meta.k) as BufferSource, 'AES-GCM', false, ['decrypt']);
      const plainBuf = await subtle().decrypt({ name: 'AES-GCM', iv: unb64(meta.iv) as BufferSource }, key, await r.arrayBuffer());
      const safeMime = /^(image\/(png|jpeg|gif|webp|avif)|video\/(mp4|webm|quicktime)|audio\/[a-z0-9.+-]+)$/i.test(meta.mime) ? meta.mime : 'application/octet-stream';
      return URL.createObjectURL(new Blob([plainBuf], { type: safeMime }));
    })().then(u => fileUrls.set(meta.id, u), () => fileUrls.set(meta.id, null)).finally(() => { pendingFiles.delete(meta.id); schedule(); });
  }
  return undefined;
}
const pendingFiles = new Set<string>();
export type Shown = { text: string; state: 'plain' | 'ok' | 'pending' | 'locked' | 'failed' };
export function view(body: string, roomId: string, authorId: string): Shown {
  if (!isEnvelope(body)) return { text: body, state: 'plain' };
  const key = roomId + '|' + authorId + '|' + body;
  const hit = plain.get(key);
  if (hit === FAIL) return { text: '', state: 'failed' };
  if (hit === LOCKED) return { text: '', state: 'locked' };
  if (hit !== undefined) return { text: hit, state: 'ok' };
  if (!pending.has(key) && apiRef) {
    pending.add(key);
    decrypt(apiRef, body, roomId, authorId, meRef).then(t => plain.set(key, t), err => plain.set(key, err?.message === 'locked' ? LOCKED : FAIL)).finally(() => { pending.delete(key); if (plain.size > 5000) plain.delete(plain.keys().next().value!); schedule(); });
  }
  return { text: '', state: identity || !loaded ? 'pending' : 'locked' };
}
let timer = 0;
function schedule() { if (timer) return; timer = window.setTimeout(() => { timer = 0; changed(); }, 16); }
export async function textFor(api: Api, body: string, roomId: string, authorId: string, meId: string) {
  if (!isEnvelope(body)) return body;
  try { return await decrypt(api, body, roomId, authorId, meId); } catch { return '🔒'; }
}

// ---------- Safety numbers and key-change warnings ----------
export async function safetyNumber(a: string, b: string) {
  const [x, y] = [a, b].sort();
  const h = new Uint8Array(await subtle().digest('SHA-256', enc.encode(x + '|' + y)));
  const out: string[] = [];
  for (let i = 0; i < 12; i++) out.push(String(((h[i * 2] << 8) | h[i * 2 + 1]) % 100000).padStart(5, '0'));
  return out;
}
const seenKey = () => 'malvo-e2ee-seen:' + account;
export function changedKeys(members: Member[]) {
  let seen: Record<string, string> = {};
  try { seen = JSON.parse(localStorage.getItem(seenKey()) || '{}'); } catch {}
  return members.filter(m => m.fp && seen[m.userId] && seen[m.userId] !== m.fp && m.userId !== meRef).map(m => m.userId);
}
export function noteKeys(members: Member[], accept = false) {
  let seen: Record<string, string> = {};
  try { seen = JSON.parse(localStorage.getItem(seenKey()) || '{}'); } catch {}
  let dirty = false;
  for (const m of members) if (m.fp && (!seen[m.userId] || accept)) { if (seen[m.userId] !== m.fp) dirty = true; seen[m.userId] = m.fp; }
  if (dirty) try { localStorage.setItem(seenKey(), JSON.stringify(seen)); } catch {}
}
